import { observer } from 'src/utils/mobx-react';
import { AppState, useAppStateContext } from 'src/AppState/AppState';
import React, { ChangeEvent, useState } from 'react';
import { action, makeAutoObservable } from 'mobx';
import { EventId } from 'src_common/common/websocket2/id/WebsocketId';

interface InnerTagType {
    key: string;
    label: string;
    values: Array<{
        id: string;
        isDefault: boolean;
        label: string;
    }>;
}

class TagSelectElementState {
    public constructor(private readonly props: TagSelectElementPropsType) {
        makeAutoObservable(this);
    }

    @action public onChangeHandler = async (event: ChangeEvent<HTMLSelectElement>): Promise<void> => {
        const value = event.target.value;
        await this.props.eventTagState.onTagChange(value, this.props.tag);
    };
}

type TagSelectElementPropsType = {
    tag: InnerTagType;
    eventId: EventId;
    eventTagState: EventTagsState;
};

const TagSelectElement = observer('TagSelectElement', (props: TagSelectElementPropsType) => {
    const [state] = useState(() => new TagSelectElementState(props));

    const event = props.eventId.getEventModel();

    if (event === null) {
        return null;
    }

    const currentSelected = event.getTag(props.tag.key);
    const tagName = `tags.${props.tag.key}.0`;

    return (
        <div className='element'>
            <div className='select-wrapper'>
                <select
                    name={tagName}
                    value={currentSelected ?? ''}
                    onChange={state.onChangeHandler}
                >
                    <option />
                    {props.tag.values.map((value) => (
                        <option
                            key={value.id}
                            value={value.id}
                        >
                            {value.label}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
});

class EventTagsState {
    public constructor(
        private readonly appState: AppState,
        private readonly eventId: () => EventId | null
    ) {
        makeAutoObservable(this);
    }

    public getEventTags = (): Array<InnerTagType> => {
        const eventId = this.eventId();
        if (eventId === null) {
            return [];
        }
        const event = eventId.getEventModel();

        if (event === null) {
            return [];
        }

        const tags = this.appState.sdkStaff.platformTags.get();
        if (tags.type === 'ready') {
            const filteredTags: Array<InnerTagType> = [];

            for (const [key, value] of Object.entries(tags.value.definitions)) {
                if (value.applicableTo.includes('event') && value.sports.includes(event.sport)) {
                    filteredTags.push({
                        key,
                        label: value.label,
                        values: value.values,
                    });
                }
            }

            filteredTags.sort((prevTag, nextTag) => (prevTag.key < nextTag.key ? -1 : 1));
            return filteredTags;
        }

        return [];
    };

    @action public onTagChange = async (tagValue: string, tag: InnerTagType): Promise<void> => {
        const eventId = this.eventId();
        if (eventId === null) {
            console.error('onTagChange error, eventId is null');
            return;
        }

        await this.appState.sdkStaff.updateEventTag(eventId, tag.key, tagValue);
        await this.appState.sdkStaff.platformTags.refresh();
    };
}

type EventTagsPropsType = {
    eventId: EventId;
};

export const EventTags = observer('EventTags', (props: EventTagsPropsType) => {
    const appState = useAppStateContext();
    const [state] = useState(() => new EventTagsState(appState, () => props.eventId));
    const eventId = props.eventId;

    return (
        <div className='event-tags-form form'>
            <div className='row'>
                <div className='col-6 event-details-box'>
                    <table>
                        <thead>
                            <tr>
                                <th>Tag name</th>
                                <th>Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {state.getEventTags().map((tag) => {
                                return (
                                    <tr key={tag.key}>
                                        <td>{tag.label}</td>
                                        <td>
                                            <TagSelectElement
                                                tag={tag}
                                                eventId={eventId}
                                                eventTagState={state}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
});
